import { useState } from 'react';
import { usePwaUpdate } from '../lib/pwa';
import { useOnlineStatus } from '../lib/useOnlineStatus';

/**
 * The app's two notices about itself, in one fixed stack at the top of the
 * window (claude files/PWA_PLAN.md):
 *
 * - **No connection.** Shown while the browser reports no network, and gone
 *   the moment it does. The app shell still runs from the service worker's
 *   cache; what does not work is anything that loads or saves, and this says
 *   so once instead of every form failing in its own words.
 * - **A new version is ready.** After a deploy reaches this device. The user
 *   picks the moment: Reload swaps in the new version; Later hides the offer
 *   until the next one (the new version also takes over once every Autopostmate
 *   tab is closed).
 *
 * Mounted once in `App`, above the routes, so sign-in pages get both. Fixed,
 * not in the page flow, because the shell is a full-height grid that a
 * banner above it would push off screen.
 */
export function StatusToasts() {
  const { needRefresh, reload, dismiss } = usePwaUpdate();
  const isOnline = useOnlineStatus();
  const [isReloading, setIsReloading] = useState(false);

  return (
    <div className="status-stack" role="status" aria-live="polite">
      {isOnline ? null : (
        <article className="undo-toast undo-toast--notice status-toast status-toast--offline">
          <p className="undo-toast__message">
            No connection. Loading and saving work again when you are back online.
          </p>
        </article>
      )}
      {needRefresh ? (
        <article className="undo-toast status-toast status-toast--update">
          <p className="undo-toast__message">A new version of Autopostmate is ready.</p>
          <div className="undo-toast__actions">
            <button
              className="button button--ghost undo-toast__undo"
              type="button"
              disabled={isReloading}
              onClick={() => {
                setIsReloading(true);
                void reload();
              }}
            >
              {isReloading ? 'Reloading' : 'Reload'}
            </button>
            <button
              className="button button--ghost undo-toast__undo"
              type="button"
              disabled={isReloading}
              onClick={dismiss}
            >
              Later
            </button>
          </div>
        </article>
      ) : null}
    </div>
  );
}
